import React, { useEffect, useState, useMemo } from 'react';
import { Card, Row, Col, Statistic, Space, Typography, Skeleton, Tag, Progress, Tooltip, Empty, Avatar, message } from 'antd';
import { FileTextOutlined, ClockCircleOutlined, CalendarOutlined, RiseOutlined, CompassOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { listActivitiesByCustomer, type ActivityDTO, type Page } from '../../services/activities';
import { renderAntdIcon } from '../../utils/antdIcon';

dayjs.extend(isBetween);
const { Text } = Typography;

interface ActivitiesOverviewProps {
  customerId?: number;
  showTitle?: boolean;
  className?: string;
  style?: React.CSSProperties;
  refreshKey?: number; // ⬅️ per forzare il refresh
}

const ActivitiesOverview: React.FC<ActivitiesOverviewProps> = ({
  customerId,
  showTitle = true,
  className,
  style,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<Page<ActivityDTO> | null>(null);

  const fetchActivities = async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      const data = await listActivitiesByCustomer(customerId, 0, 200);
      setActivities(data);
    } catch (error: any) {
      message.error(error?.message || 'Errore caricamento attività');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, refreshKey]);

  const stats = useMemo(() => {
    if (!activities?.content) {
      return {
        total: 0,
        last7: 0,
        last30: 0,
        thisMonth: 0,
        lastAt: '—',
        typeDistribution: [] as Array<{ code: string; label: string; icon?: string | null; count: number; percentage: number; }>,
        recentTrend: 0,
        mostActiveType: null as null | { code: string; label: string; icon?: string | null; count: number; percentage: number; },
        avgPerWeek: 0,
      };
    }

    const all = activities.content;
    const now = dayjs();

    const last7 = all.filter((a) => now.diff(dayjs(a.occurredAt), 'day') < 7).length;
    const last30 = all.filter((a) => now.diff(dayjs(a.occurredAt), 'day') < 30).length;
    const thisMonth = all.filter((a) => dayjs(a.occurredAt).isSame(now, 'month')).length;

    const total = activities.totalElements ?? all.length;
    const sorted = [...all].sort((a, b) => dayjs(b.occurredAt).valueOf() - dayjs(a.occurredAt).valueOf());
    const lastAt = sorted.length ? dayjs(sorted[0].occurredAt).format('DD/MM/YYYY HH:mm') : '—';

    const perTypeCount = all.reduce<Record<string, number>>((acc, a) => {
      const code = a.activityType?.code || a.type || 'EXTRA';
      acc[code] = (acc[code] || 0) + 1;
      return acc;
    }, {});

    const typeDistribution = Object.entries(perTypeCount)
      .map(([code, count]) => {
        const sample = all.find(x => (x.activityType?.code || x.type) === code)?.activityType;
        return {
          code,
          label: sample?.label || code,
          icon: sample?.icon,
          count,
          percentage: all.length ? (count / all.length) * 100 : 0,
        };
      })
      .sort((a, b) => b.count - a.count);

    const thisWeekStart = now.startOf('week');
    const lastWeekStart = thisWeekStart.subtract(1, 'week');
    const thisWeek = all.filter((a) => dayjs(a.occurredAt).isAfter(thisWeekStart)).length;
    const lastWeek = all.filter((a) => dayjs(a.occurredAt).isBetween(lastWeekStart, thisWeekStart)).length;
    const recentTrend = lastWeek > 0 ? ((thisWeek - lastWeek) / lastWeek) * 100 : 0;

    const mostActiveType = typeDistribution[0] || null;

    const fourWeeksAgo = now.subtract(4, 'week');
    const lastFourWeeks = all.filter((a) => dayjs(a.occurredAt).isAfter(fourWeeksAgo)).length;
    const avgPerWeek = lastFourWeeks / 4;

    return {
      total,
      last7,
      last30,
      thisMonth,
      lastAt,
      typeDistribution,
      recentTrend,
      mostActiveType,
      avgPerWeek,
    };
  }, [activities]);

  if (!customerId) {
    return (
      <Card className={className} style={style}>
        <Empty description="Seleziona un cliente per visualizzare le attività" />
      </Card>
    );
  }

  return (
    <Card
      className={className}
      style={{ borderRadius: 16, ...style }}
      title={
        showTitle && (
          <Space>
            <CompassOutlined />
            <span style={{ fontSize: 18, fontWeight: 600 }}>Panoramica Attività</span>
          </Space>
        )
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          {/* Key Metrics Row */}
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card size="small" style={{ borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} bodyStyle={{ padding: 16 }}>
                <Statistic
                  title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Totali</span>}
                  value={stats.total}
                  prefix={<FileTextOutlined style={{ color: 'white' }} />}
                  valueStyle={{ color: 'white', fontSize: 24, fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" style={{ borderRadius: 12, border: '1px solid #ffe4c7', background: '#fffaf2' }} bodyStyle={{ padding: 16 }}>
                <Statistic
                  title="Ultimi 7gg"
                  value={stats.last7}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ fontSize: 20, fontWeight: 'bold' }}
                  suffix={
                    stats.recentTrend !== 0 && (
                      <Tag color={stats.recentTrend > 0 ? 'success' : 'error'} style={{ marginLeft: 8, fontSize: 10 }}>
                        {stats.recentTrend > 0 ? '+' : ''}{stats.recentTrend.toFixed(0)}%
                      </Tag>
                    )
                  }
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                <Statistic
                  title="Questo mese"
                  value={stats.thisMonth}
                  prefix={<CalendarOutlined style={{ color: '#1677ff' }} />}
                  valueStyle={{ color: '#1677ff', fontSize: 20, fontWeight: 'bold' }}
                  suffix={<Text style={{ fontSize: 12, color: '#8c8c8c' }}>~{stats.avgPerWeek.toFixed(1)}/sett</Text>}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                <Statistic
                  title="Ultima attività"
                  value={stats.lastAt}
                  prefix={<RiseOutlined style={{ color: '#1d39c4' }} />}
                  valueStyle={{ color: '#1d39c4', fontSize: 16, fontWeight: 600 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Distribuzione per tipo — griglia compatta */}
          <div>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: '#595959' }}>Distribuzione per tipologia</Text>
            </div>
            {stats.typeDistribution.length ? (
              <Row gutter={[12, 12]}>
                {stats.typeDistribution.map((d) => (
                  <Col xs={24} sm={12} lg={8} key={d.code}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 12, padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Avatar style={{ background: '#f0f5ff', color: '#1677ff' }} icon={renderAntdIcon(d.icon)} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong style={{ maxWidth: '70%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {d.label}
                          </Text>
                          <Text type="secondary">{d.count} • {d.percentage.toFixed(1)}%</Text>
                        </div>
                        <Progress percent={Math.round(d.percentage)} showInfo={false} />
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <Text type="secondary">Nessun dato</Text>
            )}
          </div>
        </Space>
      )}
    </Card>
  );
};

export default ActivitiesOverview;
